<script>
	import { onMount } from 'svelte';
	import { agentsAPI } from './api.js';
	import Icon from './lib/Icon.svelte';

	let { agentId = null, template = null, providers = [], onClose, onSaved } = $props();

	let loading = $state(true);
	let saving = $state(false);
	let error = $state('');
	let activeTab = $state('identity');
	let agent = $state(null);
	let toolsCatalog = $state([]);
	let isNew = $derived(agentId === null);

	// Playground state
	let chatHistory = $state([]);
	let inputMsg = $state('');
	let testing = $state(false);
	let lastInteraction = $state(null);
	let lastKbHits = $state([]);
	let showSystemPreview = $state(false);
	let systemPreview = $state('');

	// Analytics state (only loaded for existing agents)
	let analytics = $state(null);
	let interactions = $state([]);
	let interactionsLoading = $state(false);
	let analyticsRange = $state(14);

	const TABS = [
		{ k: 'identity',   label: 'Identidad',     icon: 'user' },
		{ k: 'core',       label: 'Núcleo',        icon: 'plug' },
		{ k: 'knowledge',  label: 'Conocimiento',  icon: 'book-open' },
		{ k: 'tools',      label: 'Herramientas',  icon: 'zap' },
		{ k: 'handoff',    label: 'Handoff',       icon: 'arrow-right' },
		{ k: 'learning',   label: 'Aprendizaje',   icon: 'sparkles' }
	];

	const ROLES = [
		{ k: 'sales', l: 'Vendedor' },
		{ k: 'support', l: 'Soporte' },
		{ k: 'qualifier', l: 'Calificador' },
		{ k: 'faq', l: 'FAQ' },
		{ k: 'onboarding', l: 'Onboarding' },
		{ k: 'collections', l: 'Cobranza' },
		{ k: 'custom', l: 'Personalizado' }
	];

	function blankAgent(tpl) {
		const base = {
			name: '',
			avatar_url: null,
			role: 'custom',
			persona: '',
			description: '',
			language: 'es',
			primary_provider_id: providers[0]?.id || null,
			fallback_provider_id: null,
			temperature: 0.5,
			max_tokens: 600,
			system_prompt: '',
			greeting_message: '',
			fallback_message: '',
			guardrails: '',
			objectives: '',
			use_knowledge_base: true,
			kb_category_filter: [],
			kb_max_snippets: 4,
			allowed_tools: [],
			channels: [],
			pipeline_id: null,
			business_hours: {},
			handoff_keywords: ['agente', 'humano', 'persona'],
			handoff_after_unresolved: 3,
			auto_respond: true,
			response_delay_seconds: 2,
			learning_enabled: true,
			use_few_shot: true,
			use_memory: true,
			lessons_learned: '',
			status: 'draft',
			template_key: null,
			total_interactions: 0,
			positive_count: 0,
			negative_count: 0,
			handoff_count: 0,
			satisfaction: null
		};
		if (tpl && typeof tpl === 'object') {
			Object.assign(base, {
				name: tpl.name || '',
				role: tpl.role || 'custom',
				persona: tpl.persona || '',
				description: tpl.description || '',
				objectives: tpl.objectives || '',
				guardrails: tpl.guardrails || '',
				allowed_tools: tpl.allowed_tools || [],
				greeting_message: tpl.greeting_message || '',
				fallback_message: tpl.fallback_message || '',
				temperature: tpl.temperature ?? 0.5,
				template_key: tpl.key || null
			});
		}
		return base;
	}

	async function load() {
		loading = true;
		error = '';
		try {
			toolsCatalog = await agentsAPI.listTools();
			if (agentId) {
				agent = await agentsAPI.get(agentId);
			} else {
				agent = blankAgent(template);
			}
		} catch (e) {
			error = e.message || 'Error al cargar';
		} finally {
			loading = false;
		}
	}

	async function loadAnalytics() {
		if (!agentId) return;
		try {
			analytics = await agentsAPI.analytics(agentId, analyticsRange);
		} catch {}
	}

	async function loadInteractions() {
		if (!agentId) return;
		interactionsLoading = true;
		try {
			const res = await agentsAPI.interactions(agentId, 1);
			interactions = res.items || [];
		} finally {
			interactionsLoading = false;
		}
	}

	$effect(() => {
		if (activeTab === 'learning' && agentId) {
			loadAnalytics();
			loadInteractions();
		}
	});

	async function save() {
		saving = true;
		error = '';
		try {
			let saved;
			if (isNew) {
				saved = await agentsAPI.create(agent);
			} else {
				saved = await agentsAPI.update(agent.id, agent);
			}
			agent = saved;
			onSaved?.(saved, isNew);
		} catch (e) {
			error = e.message || 'No se pudo guardar';
		} finally {
			saving = false;
		}
	}

	function toggleTool(key) {
		const cur = new Set(agent.allowed_tools || []);
		if (cur.has(key)) cur.delete(key);
		else cur.add(key);
		agent.allowed_tools = Array.from(cur);
	}

	function isToolEnabled(key) {
		return (agent.allowed_tools || []).includes(key);
	}

	// Playground
	async function sendTest(e) {
		e?.preventDefault();
		const msg = inputMsg.trim();
		if (!msg) return;
		if (isNew) {
			error = 'Guarda el agente primero antes de probarlo.';
			return;
		}
		testing = true;
		const newHistoryUserMsg = { role: 'user', content: msg };
		chatHistory = [...chatHistory, newHistoryUserMsg];
		inputMsg = '';
		try {
			const res = await agentsAPI.test(agent.id, msg, chatHistory.slice(0, -1), { persist: false });
			if (res.error && !res.content) {
				chatHistory = [...chatHistory, { role: 'assistant', content: `⚠️ ${res.error}`, error: true }];
			} else {
				chatHistory = [...chatHistory, {
					role: 'assistant',
					content: res.content || '(sin respuesta)',
					meta: {
						provider: res.used_provider,
						latency_ms: res.latency_ms,
						kb_hits: res.kb_snippets || [],
						tool_calls: res.tool_calls || []
					}
				}];
				lastInteraction = res;
				lastKbHits = res.kb_snippets || [];
				systemPreview = res.system_prompt_preview || '';
			}
		} catch (err) {
			chatHistory = [...chatHistory, { role: 'assistant', content: `⚠️ ${err.message || 'Error'}`, error: true }];
		} finally {
			testing = false;
		}
	}

	function clearChat() {
		chatHistory = [];
		lastInteraction = null;
		lastKbHits = [];
	}

	async function rate(interactionId, rating) {
		try {
			await agentsAPI.rateInteraction(interactionId, rating);
			await loadInteractions();
			await loadAnalytics();
		} catch (e) {
			error = e.message || 'No se pudo calificar';
		}
	}

	async function markExemplar(interactionId) {
		try {
			await agentsAPI.rateInteraction(interactionId, 1, '', true);
			await loadInteractions();
		} catch (e) {
			error = e.message || 'No se pudo marcar';
		}
	}

	onMount(load);

	const STATUS_LABELS = { draft: 'Borrador', active: 'Activo', paused: 'Pausado', training: 'Entrenando' };
</script>

<div class="editor">
	{#if loading || !agent}
		<div class="loading-state">
			<p>Cargando...</p>
		</div>
	{:else}
	<!-- Header -->
	<header class="editor-head">
		<button class="back-btn" onclick={onClose}>
			<Icon name="arrow-left" size={16} />
		</button>
		<div class="head-title">
			<input
				class="name-input"
				placeholder="Nombre del agente..."
				bind:value={agent.name}
				disabled={loading}
			/>
			<div class="head-meta">
				{#if !loading}
					<span class="role-pill">{ROLES.find(r => r.k === agent?.role)?.l || agent?.role}</span>
					{#if !isNew}
						<select class="status-select" bind:value={agent.status}>
							{#each Object.entries(STATUS_LABELS) as [k, l]}
								<option value={k}>{l}</option>
							{/each}
						</select>
					{/if}
				{/if}
			</div>
		</div>
		<div class="head-actions">
			{#if error}
				<span class="err">{error}</span>
			{/if}
			<button class="btn btn-ghost btn-sm" onclick={onClose}>Cancelar</button>
			<button class="btn btn-primary btn-sm" onclick={save} disabled={saving || loading}>
				{#if saving}<span class="dot-spin"></span>{:else}<Icon name="check" size={14} />{/if}
				{saving ? 'Guardando...' : (isNew ? 'Crear agente' : 'Guardar')}
			</button>
		</div>
	</header>

	{#if loading}
		<div class="loading"><div class="spinner"></div></div>
	{:else}
		<div class="body">
			<!-- ===== Left tabs ===== -->
			<aside class="tabs">
				{#each TABS as t}
					<button
						class:active={activeTab === t.k}
						onclick={() => (activeTab = t.k)}
					>
						<Icon name={t.icon} size={15} />
						<span>{t.label}</span>
					</button>
				{/each}
			</aside>

			<!-- ===== Center form ===== -->
			<main class="form-area">
				{#if activeTab === 'identity'}
					<div class="section">
						<h3>Identidad del agente</h3>
						<p class="muted">Cómo se presenta y qué tono adopta.</p>
						<div class="form-row">
							<label>Rol
								<select bind:value={agent.role}>
									{#each ROLES as r}<option value={r.k}>{r.l}</option>{/each}
								</select>
							</label>
							<label>Idioma
								<select bind:value={agent.language}>
									<option value="es">Español</option>
									<option value="en">English</option>
									<option value="pt">Português</option>
								</select>
							</label>
						</div>
						<label>Descripción corta
							<input type="text" bind:value={agent.description} placeholder="¿En una línea, qué hace este agente?" />
						</label>
						<label>Personalidad y tono
							<textarea bind:value={agent.persona} rows="4" placeholder="Cálido, consultivo, breve. Habla de tú..."></textarea>
						</label>
						<label>Mensaje de saludo
							<textarea bind:value={agent.greeting_message} rows="2" placeholder="¡Hola! 👋 Soy..."></textarea>
						</label>
						<label>Mensaje de fallback
							<textarea bind:value={agent.fallback_message} rows="2" placeholder="No tengo esa info, te conecto con un humano..."></textarea>
						</label>
					</div>
				{:else if activeTab === 'core'}
					<div class="section">
						<h3>Núcleo de IA</h3>
						<p class="muted">Modelo principal y plan B si el primario falla.</p>
						<div class="form-row">
							<label>Núcleo principal *
								<select bind:value={agent.primary_provider_id}>
									{#each providers as p}<option value={p.id}>{p.name} — {p.model_name}</option>{/each}
								</select>
							</label>
							<label>Núcleo de respaldo
								<select bind:value={agent.fallback_provider_id}>
									<option value={null}>— Sin fallback —</option>
									{#each providers.filter(p => p.id !== agent.primary_provider_id) as p}
										<option value={p.id}>{p.name} — {p.model_name}</option>
									{/each}
								</select>
							</label>
						</div>
						<div class="form-row">
							<label>Temperatura ({agent.temperature?.toFixed(2)})
								<input type="range" min="0" max="1" step="0.05" bind:value={agent.temperature} />
								<small class="hint">0 = preciso · 1 = creativo</small>
							</label>
							<label>Tokens máximos
								<input type="number" min="100" max="4000" step="50" bind:value={agent.max_tokens} />
							</label>
						</div>
						<label>Objetivos del agente
							<textarea bind:value={agent.objectives} rows="4" placeholder="• Resolver dudas&#10;• Calificar leads&#10;• Cerrar ventas..."></textarea>
						</label>
						<label>Reglas / guardrails
							<textarea bind:value={agent.guardrails} rows="3" placeholder="No inventes precios. Si dudas, escala."></textarea>
						</label>
					</div>
				{:else if activeTab === 'knowledge'}
					<div class="section">
						<h3>Base de conocimiento</h3>
						<p class="muted">Recupera fragmentos relevantes para responder con datos verificados.</p>
						<label class="check">
							<input type="checkbox" bind:checked={agent.use_knowledge_base} />
							<span>Usar base de conocimiento</span>
						</label>
						{#if agent.use_knowledge_base}
							<div class="form-row">
								<label>Snippets máximos por respuesta
									<input type="number" min="1" max="10" bind:value={agent.kb_max_snippets} />
								</label>
							</div>
							<label>Filtrar por categorías (separadas por coma, opcional)
								<input
									type="text"
									value={(agent.kb_category_filter || []).join(', ')}
									onchange={(e) => agent.kb_category_filter = e.target.value.split(',').map(s => s.trim()).filter(Boolean)}
									placeholder="Ventas, Soporte, FAQ"
								/>
							</label>
						{/if}
					</div>
				{:else if activeTab === 'tools'}
					<div class="section">
						<h3>Herramientas</h3>
						<p class="muted">El agente sólo podrá usar las herramientas marcadas.</p>
						<div class="tools-grid">
							{#each toolsCatalog as t}
								{@const enabled = isToolEnabled(t.key)}
								<button class="tool-card" class:enabled onclick={() => toggleTool(t.key)} type="button">
									<div class="tool-check">
										{#if enabled}<Icon name="check" size={12} />{/if}
									</div>
									<div class="tool-text">
										<div class="tool-label">{t.label}</div>
										<div class="tool-desc">{t.description}</div>
										<code class="tool-key">{t.key}</code>
									</div>
								</button>
							{/each}
						</div>
					</div>
				{:else if activeTab === 'handoff'}
					<div class="section">
						<h3>Handoff a humano</h3>
						<p class="muted">Cuándo el agente debe pasar la conversación a una persona real.</p>
						<label class="check">
							<input type="checkbox" bind:checked={agent.auto_respond} />
							<span>Responder automáticamente sin esperar aprobación humana</span>
						</label>
						<label>Palabras clave que disparan handoff
							<input
								type="text"
								value={(agent.handoff_keywords || []).join(', ')}
								onchange={(e) => agent.handoff_keywords = e.target.value.split(',').map(s => s.trim()).filter(Boolean)}
								placeholder="agente, humano, persona, asesor"
							/>
						</label>
						<div class="form-row">
							<label>Intentos antes de escalar
								<input type="number" min="1" max="10" bind:value={agent.handoff_after_unresolved} />
							</label>
							<label>Retraso de respuesta (segundos)
								<input type="number" min="0" max="60" bind:value={agent.response_delay_seconds} />
							</label>
						</div>
					</div>
				{:else if activeTab === 'learning'}
					<div class="section">
						<h3>Auto-aprendizaje</h3>
						<p class="muted">El agente aprende de las interacciones bien valoradas.</p>
						<label class="check">
							<input type="checkbox" bind:checked={agent.learning_enabled} />
							<span>Aprendizaje habilitado</span>
						</label>
						<label class="check">
							<input type="checkbox" bind:checked={agent.use_few_shot} />
							<span>Inyectar ejemplos exitosos pasados (few-shot)</span>
						</label>
						<label class="check">
							<input type="checkbox" bind:checked={agent.use_memory} />
							<span>Memoria por contacto (resúmenes rodantes)</span>
						</label>
						<label>Lecciones aprendidas (instrucciones extra)
							<textarea bind:value={agent.lessons_learned} rows="3" placeholder="Cosas que aprendimos: nunca prometer entrega en menos de 3 días, etc."></textarea>
						</label>

						{#if !isNew && analytics}
							<div class="analytics card card-padded">
								<div class="an-head">
									<h4>Métricas — últimos {analyticsRange} días</h4>
									<div class="range">
										{#each [7, 14, 30] as d}
											<button class:active={analyticsRange === d} onclick={() => { analyticsRange = d; loadAnalytics(); }}>{d}d</button>
										{/each}
									</div>
								</div>
								<div class="an-grid">
									<div><div class="an-num">{analytics.totals.interactions}</div><div class="an-lbl">Interacciones</div></div>
									<div><div class="an-num">{analytics.totals.satisfaction != null ? analytics.totals.satisfaction + '%' : '—'}</div><div class="an-lbl">Satisfacción</div></div>
									<div><div class="an-num">{analytics.totals.handed_off}</div><div class="an-lbl">Handoffs</div></div>
									<div><div class="an-num">{analytics.totals.errors}</div><div class="an-lbl">Errores</div></div>
									<div><div class="an-num">{Math.round(analytics.totals.avg_latency_ms)}ms</div><div class="an-lbl">Latencia avg</div></div>
								</div>
							</div>

							<div class="interactions">
								<h4>Últimas interacciones</h4>
								{#if interactionsLoading}
									<div class="muted">Cargando...</div>
								{:else if !interactions.length}
									<div class="muted">Aún no hay interacciones registradas.</div>
								{:else}
									<ul class="int-list">
										{#each interactions as it}
											<li>
												<div class="int-msgs">
													<div class="int-user"><b>U:</b> {it.user_message}</div>
													<div class="int-ai"><b>A:</b> {it.agent_response}</div>
													{#if it.error}<div class="int-err">{it.error}</div>{/if}
												</div>
												<div class="int-actions">
													<button
														class="rate-btn"
														class:active={it.rating === 1}
														title="Bueno"
														onclick={() => rate(it.id, it.rating === 1 ? 0 : 1)}
													>👍</button>
													<button
														class="rate-btn"
														class:active={it.rating === -1}
														title="Malo"
														onclick={() => rate(it.id, it.rating === -1 ? 0 : -1)}
													>👎</button>
													<button
														class="rate-btn"
														class:active={it.is_exemplar}
														title="Marcar como ejemplo"
														onclick={() => markExemplar(it.id)}
													>⭐</button>
												</div>
											</li>
										{/each}
									</ul>
								{/if}
							</div>
						{/if}
					</div>
				{/if}
			</main>

			<!-- ===== Right playground ===== -->
			<aside class="playground">
				<div class="pg-head">
					<div>
						<h3>Probar agente</h3>
						<p class="muted">{isNew ? 'Guarda primero para probar.' : 'Conversa en vivo con tu agente.'}</p>
					</div>
					<button class="icon-btn" onclick={clearChat} title="Limpiar chat">
						<Icon name="refresh" size={14} />
					</button>
				</div>

				<div class="pg-chat">
					{#if !chatHistory.length}
						<div class="pg-empty">
							<Icon name="sparkles" size={28} />
							<p>Escribe un mensaje para empezar.</p>
						</div>
					{:else}
						{#each chatHistory as m}
							<div class="msg {m.role}" class:err={m.error}>
								<div class="bubble">{m.content}</div>
								{#if m.meta}
									<div class="msg-meta">
										<span>{m.meta.provider}</span> ·
										<span>{m.meta.latency_ms}ms</span>
										{#if m.meta.kb_hits?.length}
											· <span title={m.meta.kb_hits.map(h => h.title).join(', ')}>📚 {m.meta.kb_hits.length} KB</span>
										{/if}
										{#if m.meta.tool_calls?.length}
											· <span title={m.meta.tool_calls.map(t => t.name).join(', ')}>⚙️ {m.meta.tool_calls.length} tools</span>
										{/if}
									</div>
									{#if m.meta.tool_calls?.length}
										<div class="tool-results">
											{#each m.meta.tool_calls as tc}
												<div class="tc {tc.ok ? 'ok' : 'err'}" title={JSON.stringify(tc)}>
													<code>{tc.name}</code>
													{#if tc.ok}<span>✓</span>{:else}<span>{tc.error}</span>{/if}
												</div>
											{/each}
										</div>
									{/if}
								{/if}
							</div>
						{/each}
						{#if testing}
							<div class="msg assistant"><div class="bubble typing">...</div></div>
						{/if}
					{/if}
				</div>

				{#if lastKbHits?.length}
					<div class="pg-kb">
						<details>
							<summary>📚 KB usada ({lastKbHits.length})</summary>
							<ul>
								{#each lastKbHits as h}
									<li>{h.title}{#if h.category} · <span class="muted-sm">{h.category}</span>{/if}</li>
								{/each}
							</ul>
						</details>
					</div>
				{/if}

				{#if systemPreview}
					<div class="pg-kb">
						<details bind:open={showSystemPreview}>
							<summary>🧠 Prompt del sistema</summary>
							<pre class="prompt-preview">{systemPreview}</pre>
						</details>
					</div>
				{/if}

				<form class="pg-input" onsubmit={sendTest}>
					<input
						type="text"
						placeholder={isNew ? 'Guarda primero...' : 'Escribe un mensaje de prueba...'}
						bind:value={inputMsg}
						disabled={isNew || testing}
					/>
					<button class="btn btn-primary btn-icon" type="submit" disabled={isNew || testing || !inputMsg.trim()}>
						<Icon name="send" size={14} />
					</button>
				</form>
			</aside>
		</div>
	{/if}
	{/if}
</div>

<style>
	.editor {
		position: fixed;
		inset: 0;
		background: var(--surface-1);
		z-index: 50;
		display: flex;
		flex-direction: column;
	}

	.loading-state {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		color: var(--text-2);
	}

	.editor-head {
		height: 64px;
		display: flex; align-items: center; gap: 14px;
		padding: 0 20px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.back-btn {
		width: 36px; height: 36px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 8px;
		color: var(--text-2);
		transition: background var(--transition-fast);
	}
	.back-btn:hover { background: var(--surface-2); }

	.head-title { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
	.name-input {
		font-size: 16px;
		font-weight: 600;
		border: none !important;
		padding: 4px 0 !important;
		background: transparent !important;
	}
	.name-input:focus { box-shadow: none !important; }
	.head-meta { display: flex; gap: 8px; align-items: center; }
	.role-pill {
		font-size: 11px;
		padding: 2px 8px;
		background: var(--accent-soft);
		color: var(--accent);
		border-radius: 4px;
		font-weight: 600;
	}
	.status-select {
		width: auto !important;
		font-size: 11.5px !important;
		padding: 3px 24px 3px 8px !important;
		font-weight: 600;
		color: var(--text-2);
		background-color: var(--surface-1) !important;
	}

	.head-actions { display: flex; gap: 8px; align-items: center; }
	.err { color: var(--danger); font-size: 12px; }
	.dot-spin {
		width: 12px; height: 12px;
		border: 2px solid rgba(255,255,255,0.4);
		border-top-color: #fff;
		border-radius: 50%;
		animation: spin 600ms linear infinite;
	}

	.loading { flex: 1; display: flex; align-items: center; justify-content: center; }
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1);
		border-top-color: var(--accent);
		border-radius: 50%;
		animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	.body {
		flex: 1;
		display: grid;
		grid-template-columns: 200px minmax(0, 1fr) 380px;
		min-height: 0;
		overflow: hidden;
	}

	/* Tabs */
	.tabs {
		display: flex; flex-direction: column;
		gap: 2px;
		padding: 14px 10px;
		background: var(--surface-0);
		border-right: 1px solid var(--border-1);
	}
	.tabs button {
		display: flex; align-items: center; gap: 10px;
		padding: 9px 12px;
		font-size: 13px;
		color: var(--text-2);
		border-radius: 8px;
		text-align: left;
		transition: background var(--transition-fast), color var(--transition-fast);
	}
	.tabs button:hover { background: var(--surface-1); color: var(--text-1); }
	.tabs button.active {
		background: var(--accent-soft);
		color: var(--accent);
		font-weight: 600;
	}

	/* Form area */
	.form-area {
		overflow-y: auto;
		padding: 28px 32px;
	}
	.section {
		max-width: 640px;
		display: flex; flex-direction: column; gap: 14px;
	}
	.section h3 { font-size: 16px; margin-bottom: 4px; }
	.muted { color: var(--text-3); font-size: 13px; line-height: 1.5; margin: 0 0 8px; }
	.muted-sm { color: var(--text-3); font-size: 11.5px; }
	.section label { display: block; font-size: 12.5px; }
	.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
	.hint { display: block; font-size: 11px; color: var(--text-3); margin-top: 4px; }

	.check {
		display: flex !important;
		flex-direction: row !important;
		gap: 10px;
		align-items: center;
		padding: 10px 12px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 8px;
		cursor: pointer;
	}
	.check input { width: auto !important; }

	.tools-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
		gap: 10px;
	}
	.tool-card {
		display: flex; gap: 10px;
		padding: 12px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		text-align: left;
		cursor: pointer;
		transition: all var(--transition-fast);
	}
	.tool-card:hover { border-color: var(--border-2); }
	.tool-card.enabled {
		background: var(--accent-soft);
		border-color: var(--accent);
	}
	.tool-check {
		width: 18px; height: 18px;
		border-radius: 5px;
		border: 1.5px solid var(--border-2);
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
		color: #fff;
		background: var(--surface-0);
		margin-top: 2px;
	}
	.tool-card.enabled .tool-check { background: var(--accent); border-color: var(--accent); }
	.tool-text { min-width: 0; flex: 1; }
	.tool-label { font-size: 13px; font-weight: 600; color: var(--text-1); }
	.tool-desc { font-size: 11.5px; color: var(--text-3); margin: 3px 0; line-height: 1.4; }
	.tool-key {
		font-family: var(--font-mono);
		font-size: 10.5px;
		background: var(--surface-2);
		padding: 1px 5px;
		border-radius: 3px;
		color: var(--text-3);
	}

	.analytics .an-head {
		display: flex; align-items: center; justify-content: space-between;
		margin-bottom: 12px;
	}
	.analytics h4 { font-size: 14px; }
	.range {
		display: flex; gap: 2px;
		background: var(--surface-2);
		padding: 3px;
		border-radius: 6px;
	}
	.range button {
		padding: 3px 8px;
		font-size: 11px;
		font-weight: 600;
		color: var(--text-3);
		border-radius: 4px;
	}
	.range button.active { background: var(--surface-0); color: var(--text-1); }
	.an-grid {
		display: grid; grid-template-columns: repeat(5, 1fr);
		gap: 12px;
	}
	.an-num { font-size: 18px; font-weight: 700; color: var(--text-1); }
	.an-lbl { font-size: 10.5px; color: var(--text-3); margin-top: 2px; }

	.interactions { margin-top: 16px; }
	.interactions h4 { font-size: 14px; margin-bottom: 8px; }
	.int-list {
		list-style: none; margin: 0; padding: 0;
		display: flex; flex-direction: column; gap: 8px;
	}
	.int-list li {
		display: flex; gap: 8px;
		padding: 10px 12px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 8px;
	}
	.int-msgs { flex: 1; min-width: 0; font-size: 12.5px; line-height: 1.4; }
	.int-user { color: var(--text-2); margin-bottom: 4px; }
	.int-ai { color: var(--text-1); }
	.int-err { color: var(--danger); margin-top: 4px; font-size: 11.5px; }
	.int-actions { display: flex; flex-direction: column; gap: 4px; }
	.rate-btn {
		width: 28px; height: 28px;
		border-radius: 6px;
		border: 1px solid var(--border-1);
		background: var(--surface-0);
		font-size: 13px;
		transition: all var(--transition-fast);
	}
	.rate-btn:hover { background: var(--surface-2); }
	.rate-btn.active { background: var(--accent-soft); border-color: var(--accent); }

	/* Playground */
	.playground {
		background: var(--surface-0);
		border-left: 1px solid var(--border-1);
		display: flex;
		flex-direction: column;
		min-height: 0;
	}
	.pg-head {
		padding: 14px 16px;
		border-bottom: 1px solid var(--border-1);
		display: flex; align-items: flex-start; justify-content: space-between;
		gap: 10px;
	}
	.pg-head h3 { font-size: 14px; }
	.pg-head p { font-size: 11.5px; }
	.icon-btn {
		width: 28px; height: 28px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
	}
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }

	.pg-chat {
		flex: 1;
		overflow-y: auto;
		padding: 14px;
		display: flex; flex-direction: column;
		gap: 10px;
		background: var(--surface-1);
	}
	.pg-empty {
		flex: 1;
		display: flex; flex-direction: column;
		align-items: center; justify-content: center;
		gap: 8px;
		color: var(--text-3);
		font-size: 12.5px;
	}
	.msg { display: flex; flex-direction: column; max-width: 90%; gap: 4px; }
	.msg.user { align-self: flex-end; align-items: flex-end; }
	.msg.assistant { align-self: flex-start; }
	.bubble {
		padding: 8px 12px;
		border-radius: 12px;
		font-size: 13px;
		line-height: 1.45;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		color: var(--text-1);
		white-space: pre-wrap;
		word-wrap: break-word;
	}
	.msg.user .bubble { background: var(--accent); color: #fff; border-color: var(--accent); }
	.msg.err .bubble { background: var(--danger-soft); color: var(--danger); border-color: var(--danger-soft); }
	.bubble.typing { color: var(--text-3); font-style: italic; }
	.msg-meta { font-size: 10.5px; color: var(--text-3); }
	.tool-results {
		display: flex; flex-wrap: wrap; gap: 4px;
		margin-top: 4px;
	}
	.tc {
		display: inline-flex; align-items: center; gap: 4px;
		padding: 2px 6px;
		border-radius: 4px;
		font-size: 10.5px;
		background: var(--success-soft);
		color: var(--success);
		font-weight: 600;
	}
	.tc.err { background: var(--danger-soft); color: var(--danger); }
	.tc code {
		font-family: var(--font-mono);
		font-size: 10px;
		background: rgba(255,255,255,0.5);
		padding: 0 4px;
		border-radius: 2px;
	}

	.pg-kb {
		padding: 8px 14px;
		border-top: 1px solid var(--border-1);
		font-size: 12px;
	}
	.pg-kb summary {
		cursor: pointer;
		color: var(--text-2);
		font-weight: 500;
	}
	.pg-kb ul {
		list-style: none; padding: 8px 0 0; margin: 0;
		font-size: 11.5px;
		color: var(--text-2);
	}
	.pg-kb li { padding: 2px 0; }
	.prompt-preview {
		max-height: 280px;
		overflow: auto;
		font-family: var(--font-mono);
		font-size: 11px;
		background: var(--surface-1);
		padding: 8px;
		border-radius: 6px;
		margin-top: 8px;
		white-space: pre-wrap;
		color: var(--text-2);
	}

	.pg-input {
		padding: 12px;
		display: flex; gap: 6px;
		border-top: 1px solid var(--border-1);
		background: var(--surface-0);
	}
	.pg-input input { padding: 8px 10px; font-size: 13px; }
	.btn-icon { padding: 8px 10px !important; }

	@media (max-width: 1024px) {
		.body { grid-template-columns: 200px 1fr; }
		.playground { display: none; }
	}
</style>
